import { P2GBox, P2GButton, P2GGrid } from '@parcel2go/core-ui';
import { Suspense, useCallback, useContext, useEffect, useState } from 'react';
import { SmartSendContext } from '../../../lib/context/SmartSendContext';
import {
  ApplyStatusToSelectedAction,
  BulkSaveSelectedAction,
  ClearDeferredActionAction,
  ResetSelectionAction
} from '../../../lib/context/SmartSendContextModels';
import SmartSendSoldItemsAddAction from './SmartSendSoldItemsAddAction';
import Delete from './Delete';
import Restore from './Restore';
import SaveForLater from './SaveForLater';
import { ApplierOption } from '../../../lib/helpers/ApplierOption';
import { SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import ActionBar from './ActionBar';
import StdBar from './StdBar';
import BulkActionButton from './BulkActionButton';
import { EventSections } from '../../../lib/events/EventLogger';
import { useEventEmitter } from '../../../lib/em/Hooks';
import { BookEventType, Consts, TasksEventType } from '../../../lib/em/EmitTypes';
import Archive from './Archive';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { useAnalytics } from '@parcel2go/npm-p2g-app';
import { GenerateBaseAnalyticsPayload } from '~/lib/utils/analytics';
import { NavSmartActions } from '~/components/nav/nav-smart-actions';

interface props {
  isXs: boolean;
  smartSendItemsCache: SmartSendItemApiModel[];
  className?: string;
  filterKey: string;
  isFix?: boolean;
}

export default function SmartSendSoldItemsActionBar(props: props) {
  const { state, dispatch } = useContext(SmartSendContext);
  const [showActions, setShowActions] = useState(false);
  const [action, setAction] = useState<string>(null);
  const [confirmAction, setConfirmAction] = useState(false);
  const { emit } = useEventEmitter<BookEventType>();
  const tEmit = useEventEmitter<TasksEventType>();
  const summary = state.filters[props.filterKey].summary;
  const { track } = useAnalytics();
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);

  const logEvent = useCallback(
    (e: string) => {
      track({
        type: 'bulk_action_click',
        payload: {
          ...GenerateBaseAnalyticsPayload([EventSections.SmartSend, EventSections.ActionBar]),
          action: e
        }
      });
    },
    [track]
  );

  useEffect(() => {
    if (state.deferredAction && state.deferredAction[props.filterKey]) {
      setAction(state.deferredAction[props.filterKey]);
      setShowActions(true);
      dispatch(new ClearDeferredActionAction(props.filterKey));
    }
  }, [dispatch, state.deferredAction, props.filterKey]);

  const onSetStatusButtonClick = useCallback(
    (status: string) => {
      setConfirmAction(true);
      setAction(`Status:${status}`);
      logEvent(status);
    },
    [logEvent]
  );

  const resetSelection = useCallback(() => {
    dispatch(new ResetSelectionAction(props.filterKey));
  }, [dispatch, props.filterKey]);

  const reset = useCallback(() => {
    setAction(null);
    setConfirmAction(false);
  }, []);

  const affected = useCallback(
    (action: string) => summary.bulkOptions.filter((o) => o.includes(action)).length,
    [summary.bulkOptions]
  );

  const onActionButtonClick = useCallback(
    (a: ApplierOption) => {
      setAction(a);
      setShowActions(true);
      logEvent(a);
    },
    [logEvent]
  );

  const onActionButtonClickCallback = useCallback(
    (a: ApplierOption, close: () => void) => {
      switch (a) {
        case 'MarkAsNotPackaged':
        case 'MarkAsPackaged':
        case 'Dispatch':
          close();
          dispatch(new BulkSaveSelectedAction(props.filterKey, [a], { [a]: true }, false));
          break;
        case 'Download:PickingList':
          close();
          tEmit.emit(Consts.Task, 'Download:PickingList');
          break;
      }
      track({
        type: 'bulk_action_click',
        payload: {
          ...GenerateBaseAnalyticsPayload([EventSections.SmartSend, EventSections.BulkActionSideBar]),
          action: a
        }
      });
    },
    [dispatch, props.filterKey, tEmit, track]
  );

  return (
    <>
      {confirmAction ? (
        <ActionBar isXs={props.isXs} className={props.className}>
          <P2GBox textAlign="center" p={2}>
            {action == 'Status:Active' && (
              <Restore
                justify="center"
                affected={affected(action)}
                confirm={() => {
                  reset();
                  dispatch(new ApplyStatusToSelectedAction(props.filterKey, 'Active', false));
                }}
                cancel={reset}
              />
            )}
            {action === 'Status:Archived' && (
              <Archive
                justify="center"
                affected={affected(action)}
                confirm={() => {
                  reset();
                  dispatch(new ApplyStatusToSelectedAction(props.filterKey, 'Archived', true));
                }}
                cancel={reset}
              />
            )}
            {action === 'Status:Deleted' && (
              <Delete
                justify="center"
                affected={affected(action)}
                confirm={() => {
                  reset();
                  dispatch(new ApplyStatusToSelectedAction(props.filterKey, 'Deleted', true));
                }}
                cancel={reset}
              />
            )}
            {action === 'Status:Inactive' && (
              <SaveForLater
                justify="center"
                affected={affected(action)}
                confirm={() => {
                  reset();
                  dispatch(new ApplyStatusToSelectedAction(props.filterKey, 'Inactive', true));
                }}
                cancel={reset}
              />
            )}
          </P2GBox>
        </ActionBar>
      ) : summary.selected.length > 0 && !props.isFix ? (
        <ActionBar isXs={props.isXs} className={props.className}>
          <StdBar
            cancel={resetSelection}
            isXs={props.isXs}
            closeText={t('DESELECT_BUTTON', { defaultValue: 'Cancel' })}
          >
            {!props.isXs && (
              <>
                <BulkActionButton
                  onClick={onActionButtonClick}
                  action={'Protection'}
                  bulkOptions={summary.bulkOptions}
                />
                <BulkActionButton onClick={onActionButtonClick} action={'Extras'} bulkOptions={summary.bulkOptions} />
                <BulkActionButton
                  onClick={onActionButtonClick}
                  action={'Dimensions:Add'}
                  bulkOptions={summary.bulkOptions}
                />
                <BulkActionButton
                  onClick={onActionButtonClick}
                  action={'Service:Add'}
                  bulkOptions={summary.bulkOptions}
                />
                <BulkActionButton
                  onClick={onActionButtonClick}
                  action={'RecipientContactDetails:Add'}
                  bulkOptions={summary.bulkOptions}
                />
                <BulkActionButton
                  onClick={() => onSetStatusButtonClick('Inactive')}
                  action={'Status:Inactive'}
                  bulkOptions={summary.bulkOptions}
                />
                <BulkActionButton
                  onClick={() => onSetStatusButtonClick('Deleted')}
                  action={'Status:Deleted'}
                  bulkOptions={summary.bulkOptions}
                />
                <BulkActionButton
                  onClick={() => onSetStatusButtonClick('Archived')}
                  action={'Status:Archived'}
                  bulkOptions={summary.bulkOptions}
                />
                <BulkActionButton
                  onClick={() => onSetStatusButtonClick('Active')}
                  action={'Status:Active'}
                  bulkOptions={summary.bulkOptions}
                />
                <BulkActionButton
                  onClick={onActionButtonClick}
                  action={'Download:CSV:Sold'}
                  bulkOptions={summary.bulkOptions}
                />
                <BulkActionButton
                  onClick={onActionButtonClick}
                  action={'Dispatch:Confirm'}
                  bulkOptions={summary.bulkOptions}
                />
                <BulkActionButton
                  onClick={onActionButtonClick}
                  action={'Download:CSV:Booked'}
                  bulkOptions={summary.bulkOptions}
                />
                <BulkActionButton
                  onClick={onActionButtonClick}
                  action={'Download:CSV:Orders'}
                  bulkOptions={summary.bulkOptions}
                />
                <BulkActionButton
                  onClick={onActionButtonClick}
                  action={'Download:Labels'}
                  bulkOptions={summary.bulkOptions}
                />
              </>
            )}
            <BulkActionButton
              onClick={onActionButtonClick}
              action={'LIST'}
              label={
                props.isXs
                  ? t('ACTIONS_BUTTON', { defaultValue: 'Actions' })
                  : t('MORE_ACTIONS_BUTTON', { defaultValue: 'More Actions' })
              }
            />
            <P2GGrid item flexShrink={1}>
              {summary.bulkOptions.includes('Book') && (
                <>
                  <P2GBox
                    onClick={() => {
                      logEvent('book');
                      emit(Consts.Book, { Open: true, Ids: [] });
                    }}
                  >
                    {props.isXs ? (
                      <P2GButton variant="primary">{t('BOOK_BUTTON', { defaultValue: 'Book' })}</P2GButton>
                    ) : (
                      <P2GButton variant="primary">
                        {t('BOOK_SELECTED_ITEMS_BUTTON', {
                          defaultValue: 'Book {{count}} Item(s)',
                          count: summary.bulkOptions.filter((x) => x === 'Book').length
                        })}
                      </P2GButton>
                    )}
                  </P2GBox>
                </>
              )}
            </P2GGrid>
          </StdBar>
        </ActionBar>
      ) : (
        <Suspense>
          <NavSmartActions
            isXs={props.isXs}
            wrapper={(x) => (
              <ActionBar isXs={props.isXs} className={props.className} fullWidth>
                <P2GBox p={2} textAlign="left" sx={{ width: '100%' }}>
                  {x}
                </P2GBox>
              </ActionBar>
            )}
            limitToConcernTypes={[]}
            contained={true}
          />
        </Suspense>
      )}
      <SmartSendSoldItemsAddAction
        filterKey={props.filterKey}
        smartSendItemsCache={props.smartSendItemsCache}
        open={showActions}
        onClose={() => {
          setShowActions(false);
          setAction(null);
        }}
        onActionClick={onActionButtonClickCallback}
        filter={summary}
        action={action}
        exclude={
          props.isXs
            ? ['Book', 'Download:Labels']
            : // : ['Book', 'Download:Labels', 'Status:Active', 'Status:Inactive', 'Status:Deleted']
              ['Book', 'Download:Labels']
        } /// These are handled in this file
      />
    </>
  );
}
